import { Link } from "react-router-dom";
import type { Entry } from "@/mocks/entries";
import { formatHot, getCategoryById } from "@/hooks/useSiteData";

interface EntryCardProps {
  entry: Entry;
  rank?: number;
  layout?: "grid" | "row";
}

function statusClass(status: string): string {
  if (status === "持续发酵") return "bg-accent-100 text-accent-800 border-accent-200";
  if (status === "已回应") return "bg-secondary-100 text-secondary-800 border-secondary-200";
  return "bg-background-100 text-foreground-700 border-background-300";
}

export default function EntryCard({ entry, rank, layout = "grid" }: EntryCardProps) {
  const category = getCategoryById(entry.categoryId);

  if (layout === "row") {
    return (
      <Link
        to={`/entry/${entry.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/60 md:p-4"
      >
        <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-28 md:w-40">
          <img
            src={entry.cover}
            alt={`${entry.name} · ${entry.title} 话题配图`}
            title={`${entry.name} 词条配图 `}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            {typeof rank === "number" && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-foreground-900 text-[11px] font-medium text-background-50">
                {rank}
              </span>
            )}
            <span className="text-xs text-secondary-700">{category?.name}</span>
            <span className="text-xs text-foreground-400">· {entry.updatedAt}</span>
          </div>
          <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-700 md:text-base">
            {entry.title}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
            {entry.summary}
          </p>
          <div className="mt-auto flex items-center gap-3 pt-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {formatHot(entry.hot)}
            </span>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] ${statusClass(entry.status)}`}>
              {entry.status}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/entry/${entry.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-200">
        <img
          src={entry.cover}
          alt={`${entry.name} · ${entry.title} 话题配图`}
          title={`${entry.name} 词条配图`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-0.5 text-[11px] font-medium text-accent-800">
          {category?.name}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
          {entry.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{entry.summary}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {entry.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500"></i>
            {formatHot(entry.hot)} 热度
          </span>
          <span className="flex items-center gap-1 text-primary-700">
            查看词条
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </Link>
  );
}